import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.doubleOrNull
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import kotlin.math.roundToInt

@Composable
fun ChallengesScreen(token: String, ownParticipantId: String) {
    val state = rememberAsyncState()
    var rows by remember { mutableStateOf<List<ChallengeProgress>>(emptyList()) }
    var definitions by remember { mutableStateOf<Map<String, JsonObject>>(emptyMap()) }
    var exerciseNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var tierNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var showFinished by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        state.run {
            rows = Api.fetchChallengeProgress(token)
            definitions = Api.fetchChallenges(token).associate { it.id to it.definition }
            exerciseNames = Api.fetchExerciseTypes(includeInactive = true).associate { it.id to it.name }
            tierNames = Api.fetchRatingTiers().associate { it.id to it.label }
        }
    }

    Div(attrs = { classes("tab-row") }) {
        NavButton("Aktuální", !showFinished) { showFinished = false }
        NavButton("Dokončené", showFinished) { showFinished = true }
    }
    ErrorMessage(state.error)
    if (!state.loaded) {
        LoadingText()
        return
    }

    val groups = rows.filter { it.isFinal == showFinished }
        .groupBy { it.challengeId }
        .values
        .sortedByDescending { it.first().startsOn }
    if (groups.isEmpty()) {
        P { Text(if (showFinished) "Zatím nebyla dokončena žádná výzva." else "Právě neprobíhá žádná výzva.") }
        return
    }
    groups.forEach { group ->
        ChallengeCard(
            rows = group.sortedWith(compareBy<ChallengeProgress> { row -> row.rank }.thenBy { row -> row.participantName }),
            ownParticipantId = ownParticipantId,
            definition = definitions[group.first().challengeId],
            exerciseNames = exerciseNames,
            tierNames = tierNames,
        )
    }
}

@Composable
private fun ChallengeCard(
    rows: List<ChallengeProgress>,
    ownParticipantId: String,
    definition: JsonObject?,
    exerciseNames: Map<String, String>,
    tierNames: Map<String, String>,
) {
    val challenge = rows.first()
    val own = rows.firstOrNull { it.participantId == ownParticipantId }
    Div(attrs = { classes("challenge-card") }) {
        Div(attrs = { classes("challenge-head") }) {
            Div {
                H3 { Text(challenge.challengeName) }
                P(attrs = { classes("challenge-range") }) {
                    Text(formatCzechDateRange(challenge.startsOn, challenge.endsOn))
                    if (!challenge.isFinal && challenge.endsOn < todayIso()) Text(" · čeká na automatické vyhodnocení")
                }
            }
            if (challenge.isFinal) Span(attrs = { classes("challenge-final") }) { Text("Uzavřeno") }
        }
        if (challenge.description.isNotBlank()) P(attrs = { classes("challenge-description") }) { Text(challenge.description) }

        if (!challenge.isFinal && definition != null) {
            val conditions = challengeMedalConditions(definition, exerciseNames, tierNames)
            if (conditions.isNotEmpty()) {
                Div(attrs = { classes("challenge-conditions") }) {
                    P(attrs = { classes("challenge-conditions-title") }) { Text("Podmínky medailí") }
                    conditions.forEach { (medal, condition) ->
                        Div(attrs = { classes("challenge-condition") }) {
                            MedalBadge(medal)
                            Span { Text(condition) }
                        }
                    }
                }
            }
        }

        own?.let { progress ->
            Div(attrs = { classes("challenge-own") }) {
                Div(attrs = { classes("challenge-own-line") }) {
                    Span { Text(if (progress.isFinal) "Tvůj výsledek" else "Tvůj průběžný výsledek") }
                    MedalBadge(progress.medal)
                }
                Div(attrs = { classes("challenge-progress-copy") }) {
                    Text(progress.progressText)
                    progress.nextMedal?.let { Text(" · další: ${medalIcon(it)} ${medalLabel(it).lowercase()}") }
                }
                val target = progress.nextTarget
                if (!progress.isFinal && target != null && target > 0) {
                    val percent = (progress.progress / target * 100.0).coerceIn(0.0, 100.0).roundToInt()
                    Div(attrs = {
                        classes("challenge-progress-track")
                        attr("role", "progressbar")
                        attr("aria-valuenow", percent.toString())
                    }) {
                        Div(attrs = {
                            classes("challenge-progress-fill")
                            style { width(percent.percent) }
                        }) {}
                    }
                }
            }
        }

        Div(attrs = { classes("table-scroll") }) {
            Table(attrs = { classes("challenge-board") }) {
                Thead { Tr { Th { Text("#") }; Th { Text("Účastník") }; Th { Text("Medaile") }; Th { Text("Postup") } } }
                Tbody {
                    rows.forEach { row ->
                        Tr(attrs = { if (row.participantId == ownParticipantId) classes("challenge-me") }) {
                            Td { Text(row.rank.toString()) }
                            Td { Text(row.participantName) }
                            Td { MedalBadge(row.medal) }
                            Td { Text(row.progressText) }
                        }
                    }
                }
            }
        }
    }
}

private fun challengeMedalConditions(
    definition: JsonObject,
    exerciseNames: Map<String, String>,
    tierNames: Map<String, String>,
): List<Pair<String, String>> {
    val metric = definition["metric"] as? JsonObject ?: return emptyList()
    val medals = definition["medals"] as? JsonObject ?: return emptyList()
    val metricKind = (metric["kind"] as? JsonPrimitive)?.content ?: return emptyList()
    val exerciseIds = (metric["exercise_type_ids"] as? JsonArray).orEmpty()
        .mapNotNull { (it as? JsonPrimitive)?.content }
    val selectedExercises = exerciseIds.mapNotNull(exerciseNames::get)
    val exerciseSuffix = if (selectedExercises.isEmpty()) "" else " · pouze ${selectedExercises.joinToString(", ")}"
    val requiredTier = (metric["rating_tier_id"] as? JsonPrimitive)?.content?.let(tierNames::get)
    val dailyPoints = (metric["daily_points"] as? JsonPrimitive)?.doubleOrNull
    val weekdays = (metric["weekdays"] as? JsonArray).orEmpty()
        .mapNotNull { (it as? JsonPrimitive)?.content?.toIntOrNull() }
        .mapNotNull(::weekdayName)
        .joinToString(" a ")

    return listOf("bronze", "silver", "gold").mapNotNull { medal ->
        val medalDefinition = medals[medal] as? JsonObject ?: return@mapNotNull null
        val target = medalDefinition["target"] as? JsonObject ?: return@mapNotNull null
        val text = if (metricKind == "rating_on_weekdays") {
            val targetTier = (target["rating_tier_id"] as? JsonPrimitive)?.content?.let(tierNames::get)
                ?: return@mapNotNull null
            "$targetTier v ${weekdays.ifBlank { "určené dny" }}$exerciseSuffix"
        } else {
            val value = (target["value"] as? JsonPrimitive)?.doubleOrNull ?: return@mapNotNull null
            when (metricKind) {
                "rating_days_count" ->
                    "${formatNumber(value)} dní s hodnocením ${requiredTier ?: "požadované úrovně"} nebo vyšším$exerciseSuffix"
                "total_points" -> "${formatNumber(value)} bodů celkem"
                "exercise_points" -> "${formatNumber(value)} bodů${if (selectedExercises.isEmpty()) "" else " z ${selectedExercises.joinToString(", ")}"}"
                "daily_exercise_minimum" ->
                    "${formatNumber(value)} dní s alespoň ${dailyPoints?.let(::formatNumber) ?: "?"} body za den$exerciseSuffix"
                else -> return@mapNotNull null
            }
        }
        medal to text
    }
}

private fun weekdayName(day: Int): String? = when (day) {
    1 -> "pondělí"
    2 -> "úterý"
    3 -> "středu"
    4 -> "čtvrtek"
    5 -> "pátek"
    6 -> "sobotu"
    7 -> "neděli"
    else -> null
}
